/**
 * Input formatting utilities for card data
 */

import { detectCardType } from './cardTypeDetector';

/**
 * Formats a card number with spaces every 4 digits
 * @param value - Raw card number input
 * @returns Formatted card number
 */
export function formatCardNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Detect card type for special formatting
  const cardType = detectCardType(digits);

  // American Express format: 4-6-5
  if (cardType === 'amex') {
    if (digits.length <= 4) return digits;
    if (digits.length <= 10) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 10)} ${digits.slice(10, 15)}`;
  }

  // Default format: 4-4-4-4 (or 4-4-4-4-3 for 19-digit cards)
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4));
  }
  return groups.join(' ');
}

/**
 * Formats expiry date as MM/YY
 * Handles both digit-stream input (e.g. "1225") and slash-first input (e.g. "1/25").
 * When the user types a slash after a single digit month (e.g. "1/"), the month is
 * zero-padded to produce "01/", so "1/25" → "01/25" rather than "12/5".
 * @param value - Raw expiry input
 * @returns Formatted expiry date (max 5 chars: MM/YY)
 */
export function formatExpiryDate(value: string): string {
  // If the user typed a slash, respect it to support M/YY input (e.g. "1/25" → "01/25")
  if (value.includes('/')) {
    const slashIndex = value.indexOf('/');
    const monthDigits = value.slice(0, slashIndex).replace(/\D/g, '').slice(0, 2);
    const yearDigits = value.slice(slashIndex + 1).replace(/\D/g, '').slice(0, 2);

    if (monthDigits.length === 0) return '';

    // Zero-pad single-digit month when slash is explicitly present
    const paddedMonth = monthDigits.length === 1 ? `0${monthDigits}` : monthDigits;

    if (yearDigits.length === 0) return `${paddedMonth}/`;
    return `${paddedMonth}/${yearDigits}`;
  }

  // Digit-stream path: no slash typed yet
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;

  // Format as MM/YY
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
}

/**
 * Parses formatted expiry date into month and year
 * @param formattedDate - Expiry date in MM/YY or MM/YYYY format
 * @returns Object with month and year, or null if invalid
 */
export function parseExpiryDate(formattedDate: string): { month: string; year: string } | null {
  const parts = formattedDate.split('/');
  if (parts.length !== 2) return null;

  const month = parts[0]?.trim();
  let year = parts[1]?.trim();

  if (!month || !year) return null;

  // Convert 2-digit year to 4-digit year
  if (year.length === 2) {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    year = (currentCentury + parseInt(year, 10)).toString();
  }

  return { month, year };
}

/**
 * Formats CVV (just ensures it's digits only)
 * @param value - Raw CVV input
 * @param maxLength - Maximum CVV length (3 or 4)
 * @returns Formatted CVV
 */
export function formatCvv(value: string, maxLength: number = 3): string {
  const digits = value.replace(/\D/g, '');
  return digits.slice(0, maxLength);
}

/**
 * Formats phone number as (XXX) XXX-XXXX
 * @param value - Raw phone number input
 * @returns Formatted phone number
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Formats postal code (US ZIP or Canadian postal code)
 * @param value - Raw postal code input
 * @param country - Country code ('US' or 'CA')
 * @returns Formatted postal code
 */
export function formatPostalCode(value: string, country: string = 'US'): string {
  if (country === 'CA') {
    // Canadian postal code: A1A 1A1
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (cleaned.length <= 3) return cleaned;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
  }

  // US ZIP code: 12345 or 12345-6789
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
}

/**
 * Capitalizes first letter of each word
 * @param value - Input string
 * @returns Capitalized string
 */
export function capitalizeWords(value: string): string {
  return value
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formats cardholder name (capitalizes each word)
 * @param value - Raw name input
 * @returns Formatted name
 */
export function formatCardholderName(value: string): string {
  // Remove extra spaces and non-letter characters except spaces, hyphens, apostrophes
  const cleaned = value.replace(/[^a-zA-Z\s'-]/g, '').replace(/\s+/g, ' ').trim();
  return capitalizeWords(cleaned);
}
